// Bridges the WASM RustSimAdapter into the canonical GameDOMAdapter interface
// from @blueforge-studio/game-adapter.
//
// The WASM crate exposes `snapshot()`, `query()`, `intent()`, `drainEvents()`,
// `step()`, `destroy()`. This file:
//   1. Adds a typed JS event-emitter so consumers can call `subscribe()`.
//   2. Maps glob-ish patterns ('damage', 'entity:*') to event-type filters.
//   3. Provides typed wrappers so TS callers never see `JsValue`.

import type {
  GameEvent,
  GameIntent,
  GameSnapshot,
  IntentResponse,
  Ruleset,
  SceneNode,
  Seat,
  Selector,
} from '@aod/protocol';
import type { GameDOMAdapter } from '@blueforge-studio/game-adapter';

/** Construction options for the Rust sim adapter. */
export interface CreateRustSimAdapterOptions {
  /** App identifier — appears in `GameSnapshot.meta.appId`. */
  readonly appId: string;
  /** Match seed. Default 0. */
  readonly matchSeed?: number | bigint;
  /**
   * Populate the world with the demo match (16×12 hex grid, 2 players, 2 cities).
   * Default true. Set false to construct an empty world (e.g., for replay restore).
   */
  readonly setupDemo?: boolean;
  /** JSON `GameConfig` overrides; forwarded as `configJson`. */
  readonly configJson?: string;
  /**
   * Match ruleset from the setup screen (play-modes-and-rulesets spec §11).
   * Forwarded as `ruleset`; the sim ignores it until roadmap R1 reads it.
   */
  readonly ruleset?: Ruleset;
  /** Seats (seat 0 = local human); forwarded as `seats`, read from R1 on. */
  readonly seats?: readonly Seat[];
  /** Team of each seat (multiplayer teams); forwarded as `teams`. */
  readonly teams?: readonly number[];
  /**
   * An authored `aod-map` document (JSON). Forwarded as `map`: the sim
   * builds the board from the document instead of generating one, and
   * requires a `ruleset` and `seats` alongside it.
   */
  readonly map?: string;
}

/**
 * The minimal shape we need from the WASM module. The wasm-pack output
 * provides exactly this surface from `packages/sim-wasm/pkg/aod_sim_wasm.js`.
 */
/**
 * One city suggestion from the wasm `suggestIntents` API (play-modes spec
 * §8, `suggest` auto-manage mode): a deliberated-but-unexecuted economy
 * intent. Only `build_structure` / `train_unit` ever come back (§7.4).
 */
export interface CitySuggestion {
  function_name: 'build_structure' | 'train_unit';
  /** `build_structure`: `{ city_id, structure_id }`; `train_unit`: `{ city_id, unit_kind }`. */
  args: {
    city_id?: number;
    structure_id?: string;
    unit_kind?: string;
    [key: string]: unknown;
  };
  priority?: number;
  dice_trait_value?: number;
  source_role?: string;
  player_id?: number;
  /** Deliberator rationale — surfaced as the button's tooltip. */
  reasoning?: string | null;
}

export interface RustSimWasmHandle {
  new (options: unknown): RustSimWasmInstance;
}

export interface RustSimWasmInstance {
  step(n: number): void;
  tick(): bigint;
  matchSeed(): bigint;
  determinismHash(): bigint;
  snapshot(): GameSnapshot;
  query(selector: Selector | undefined): SceneNode[];
  intent(intent: GameIntent): IntentResponse;
  drainEvents(): GameEvent[];
  /**
   * Re-seed and rebuild the demo match in-place. The wasm-bindgen-generated
   * binding requires a BigInt — the wrapper coerces numbers for callers.
   */
  reset(newSeed: bigint): void;
  /** Full game state as JSON (wasm-bindgen keeps the Rust name). */
  save_game(): string;
  /** Restore state from `save_game` output; throws (world untouched) on a bad save. */
  load_game(json: string): void;
  destroy(): void;
  free?(): void;
  // Lockstep
  setLockstep(enabled: boolean): void;
  setExpectedPlayers(count: number): void;
  playerReady(playerId: number): void;
  canAdvance(): boolean;
  isLockstep(): boolean;
  /** Compute rich AI state snapshot for a player (units, cities, heroes, ruins, etc.). */
  aiSnapshot(playerId: number): unknown;
  /** Submit external AI intents (from LLM) for a player before a tick. */
  submitExternalIntents(playerId: number, intents: unknown[]): void;
  /** List all registered AI functions with metadata (for LLM tool definitions). */
  listFunctions(playerId: number): unknown[];
  /**
   * Execute one AI function for `playerId` without recording an action-log
   * entry. Only read-only (`query`-category) functions are accepted — a call
   * that changes the world has to ride the lockstep frame instead.
   */
  callFunction(playerId: number, name: string, role: string, args: unknown): unknown;
  /** Build a minimap snapshot for a player, respecting fog of war. */
  minimapSnapshot(playerId: number): unknown;
  /** Assemble player HUD data (power scores, info levels) for all opponents. */
  playerHud(playerId: number): unknown;
  /**
   * Top city build/train suggestions for a human seat in `suggest`
   * auto-manage mode (spec §8): up to 2 economy-domain intents, not
   * executed. Empty unless that seat's `autoManageCities` is `"suggest"`.
   */
  suggestIntents(playerId: number): CitySuggestion[];
}

/**
 * Create a `GameDOMAdapter` backed by the WASM Rust simulation.
 *
 * @param wasm The class exported by `@aod/sim-wasm` (after `await init()`).
 * @param options Adapter options.
 */
export function createRustSimAdapter(
  wasm: RustSimWasmHandle,
  options: CreateRustSimAdapterOptions,
): GameDOMAdapter & {
  /** Advance the simulation by `n` ticks. */
  step(n: number): void;
  /** Current tick counter. */
  tick(): bigint;
  /** Determinism hash (replay validation). */
  determinismHash(): bigint;
  /** Reset the world to a fresh demo match with `newSeed`. */
  reset(newSeed: number | bigint): void;
  /** Serialize the full game state to a JSON string. */
  saveGame(): string;
  /**
   * Restore a state produced by `saveGame`. Throws, leaving the world
   * untouched, when the input isn't a loadable save.
   */
  loadGame(json: string): void;
  /** Enable or disable lockstep mode. */
  setLockstep(enabled: boolean): void;
  /** Configure how many players must call `playerReady` before tick can advance. */
  setExpectedPlayers(count: number): void;
  /** Mark a player as ready for the current tick (lockstep mode). */
  playerReady(playerId: number): void;
  /** Returns true if all expected players have called `playerReady` for this tick. */
  canAdvance(): boolean;
  /** Returns true if lockstep mode is active. */
  isLockstep(): boolean;
  /** Build a minimap snapshot for a player, respecting fog of war. */
  minimapSnapshot(playerId: number): unknown;
  /** Assemble player HUD data (power scores, info levels) for all opponents. */
  playerHud(playerId: number): unknown;
  /**
   * Top city build/train suggestions for a human seat in `suggest`
   * auto-manage mode (spec §8): up to 2 economy-domain intents, not
   * executed. Empty unless that seat's `autoManageCities` is `"suggest"`.
   */
  suggestIntents(playerId: number): CitySuggestion[];
} {
  const seed =
    options.matchSeed === undefined
      ? 0
      : typeof options.matchSeed === 'bigint'
        ? Number(options.matchSeed) // wasm-bindgen u64 accepts number-or-bigint
        : options.matchSeed;

  const instance = new wasm({
    appId: options.appId,
    matchSeed: seed,
    setupDemo: options.setupDemo ?? true,
    ...(options.configJson ? { configJson: options.configJson } : {}),
    ...(options.ruleset ? { ruleset: options.ruleset } : {}),
    ...(options.seats ? { seats: options.seats } : {}),
    ...(options.teams ? { teams: options.teams } : {}),
    ...(options.map ? { map: options.map } : {}),
  });

  // Local subscriber registry. WASM doesn't call into JS callbacks; we
  // poll `drainEvents()` on each step and dispatch matching subscribers.
  type Subscription = {
    pattern: string;
    cb: (event: GameEvent) => void;
  };
  const subscriptions = new Set<Subscription>();

  const matchPattern = (pattern: string, type: string): boolean => {
    if (pattern === '*' || pattern === '') return true;
    if (pattern.endsWith(':*')) return type.startsWith(pattern.slice(0, -2));
    return pattern === type;
  };

  const dispatchEvents = (events: readonly GameEvent[]): void => {
    if (events.length === 0 || subscriptions.size === 0) return;
    for (const event of events) {
      for (const sub of subscriptions) {
        if (matchPattern(sub.pattern, event.type)) sub.cb(event);
      }
    }
  };

  return {
    snapshot(): GameSnapshot {
      return instance.snapshot();
    },

    query(selector: Selector): SceneNode[] {
      return instance.query(selector);
    },

    intent(intent: GameIntent): IntentResponse {
      const response = instance.intent(intent);
      // Even successful intents may produce events — surface them to subscribers.
      if (response.ok && response.events.length > 0) {
        dispatchEvents(response.events);
      }
      return response;
    },

    subscribe(pattern: string, cb: (event: GameEvent) => void): () => void {
      const sub: Subscription = { pattern, cb };
      subscriptions.add(sub);
      return () => {
        subscriptions.delete(sub);
      };
    },

    destroy(): void {
      subscriptions.clear();
      instance.destroy();
      // wasm-bindgen-generated handle exposes free() — release WASM memory eagerly.
      instance.free?.();
    },

    // ----- AOD extensions (not part of GameDOMAdapter) --------------------
    step(n: number): void {
      instance.step(n);
      dispatchEvents(instance.drainEvents());
    },
    tick(): bigint {
      return instance.tick();
    },
    determinismHash(): bigint {
      return instance.determinismHash();
    },
    reset(newSeed: number | bigint): void {
      // wasm-bindgen marshals `u64` as JS BigInt at the call boundary —
      // passing a plain number throws "Cannot convert N to a BigInt".
      // Coerce here so callers can keep passing numbers freely.
      const asBigInt = typeof newSeed === 'bigint' ? newSeed : BigInt(newSeed);
      instance.reset(asBigInt);
    },
    saveGame(): string {
      return instance.save_game();
    },
    loadGame(json: string): void {
      // A friendly check for obviously wrong files; the sim (save format v2)
      // does the real validation and throws, leaving the world untouched,
      // on anything it can't load (bad version, truncated, corrupt).
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        throw new Error('Not an Ages of Dominion save (not JSON)');
      }
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        (parsed as { format?: unknown }).format !== 'aod-save'
      ) {
        throw new Error('Not an Ages of Dominion save');
      }
      instance.load_game(json);
    },
    setLockstep(enabled: boolean): void {
      instance.setLockstep(enabled);
    },
    setExpectedPlayers(count: number): void {
      instance.setExpectedPlayers(count);
    },
    playerReady(playerId: number): void {
      instance.playerReady(playerId);
    },
    canAdvance(): boolean {
      return instance.canAdvance();
    },
    isLockstep(): boolean {
      return instance.isLockstep();
    },
    minimapSnapshot(playerId: number): unknown {
      return instance.minimapSnapshot(playerId);
    },
    playerHud(playerId: number): unknown {
      return instance.playerHud(playerId);
    },
    suggestIntents(playerId: number): CitySuggestion[] {
      return instance.suggestIntents(playerId);
    },
  };
}
